import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';

/**
 * FilloutContext - Centralized contact form popup management
 * 
 * This context provides a single, stable system for opening Fillout popups
 * across the entire application. It solves several issues:
 * 
 * 1. Script is loaded once at app level and never removed
 * 2. Uses Fillout's official API (window.Fillout.openPopup) instead of DOM hacks
 * 3. Works reliably across SPA navigation without requiring page refresh
 * 4. Handles edge cases like rapid navigation and multiple open attempts
 */

const FILLOUT_SCRIPT_URL = 'https://server.fillout.com/embed/v1/';
const FILLOUT_FORM_ID = 'd16QMXrZHJus';

const FilloutContext = createContext(null);

export const FilloutProvider = ({ children }) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const scriptLoadedRef = useRef(false);
  const initAttempts = useRef(0);

  // Load Fillout script once on mount - never remove it
  useEffect(() => {
    if (scriptLoadedRef.current) return;

    const loadScript = () => {
      // Check if script already exists
      const existingScript = document.querySelector(`script[src="${FILLOUT_SCRIPT_URL}"]`);
      
      if (existingScript) {
        scriptLoadedRef.current = true;
        setIsScriptLoaded(true);
        return;
      }

      // Create and append script
      const script = document.createElement('script');
      script.src = FILLOUT_SCRIPT_URL;
      script.async = true;
      
      script.onload = () => {
        scriptLoadedRef.current = true;
        setIsScriptLoaded(true);
      };

      script.onerror = () => {
        console.error('Failed to load Fillout script');
        // Retry after delay
        if (initAttempts.current < 3) {
          initAttempts.current += 1;
          setTimeout(loadScript, 1000);
        }
      };

      document.head.appendChild(script);
    };

    loadScript();

    // No cleanup - we want the script to persist.
    // Module-level constants are intentionally not declared as deps (mount-only side effect).
    // Intentional: mount-only effect; deps intentionally omitted
  }, []);

  // Open popup using Fillout's official API
  const openPopup = useCallback(() => {
    // Prevent multiple rapid opens
    if (isPopupOpen) {
      return;
    }

    const tryOpen = (attempts = 0) => {
      // Method 1: Try official Fillout API
      if (window.Fillout && typeof window.Fillout.openPopup === 'function') {
        try {
          window.Fillout.openPopup(FILLOUT_FORM_ID);
          setIsPopupOpen(true);
          
          // Reset popup state after a delay (popup close detection)
          setTimeout(() => setIsPopupOpen(false), 500);
          return true;
        } catch (e) {
          console.warn('Fillout.openPopup failed:', e);
        }
      }

      // Method 2: Try the older Fillout.open API
      if (window.Fillout && typeof window.Fillout.open === 'function') {
        try {
          window.Fillout.open(FILLOUT_FORM_ID);
          setIsPopupOpen(true);
          setTimeout(() => setIsPopupOpen(false), 500);
          return true;
        } catch (e) {
          console.warn('Fillout.open failed:', e);
        }
      }

      // Method 3: Find and click the Fillout-generated button
      const container = document.querySelector(`[data-fillout-id="${FILLOUT_FORM_ID}"]`);
      if (container) {
        // Fillout creates a button inside the container
        const button = container.querySelector('button');
        if (button) {
          button.click();
          setIsPopupOpen(true);
          setTimeout(() => setIsPopupOpen(false), 500);
          return true;
        }
      }

      // Retry if script might still be initializing
      if (attempts < 5) {
        setTimeout(() => tryOpen(attempts + 1), 150);
        return false;
      }

      console.error('Could not open Fillout popup after multiple attempts');
      return false;
    };

    // Small delay to ensure any navigation has settled
    setTimeout(() => tryOpen(), 50);
  }, [isPopupOpen]);

  // Close popup (if Fillout provides this API)
  const closePopup = useCallback(() => {
    if (window.Fillout && typeof window.Fillout.closePopup === 'function') {
      try {
        window.Fillout.closePopup();
      } catch (e) {
        console.warn('Fillout.closePopup failed:', e);
      }
    }
    setIsPopupOpen(false);
  }, []);

  // Memoise context value to avoid unnecessary re-renders of consumers
  const contextValue = useMemo(() => ({
    openPopup,
    closePopup,
    isScriptLoaded,
    isPopupOpen,
    formId: FILLOUT_FORM_ID,
  }), [openPopup, closePopup, isScriptLoaded, isPopupOpen]);

  return (
    <FilloutContext.Provider value={contextValue}>
      {children}
      {/* Global Fillout embed container - always present in DOM */}
      <div
        data-fillout-id={FILLOUT_FORM_ID}
        data-fillout-embed-type="popup"
        data-fillout-dynamic-resize
        data-fillout-button-color="#008080"
        data-fillout-inherit-parameters
        data-fillout-popup-size="medium"
        data-fillout-button-text=""
        style={{ display: 'none' }}
        aria-hidden="true"
      />
    </FilloutContext.Provider>
  );
};

/**
 * Hook to access Fillout popup functionality
 * 
 * Usage:
 * const { openPopup } = useFilloutPopup();
 * <button onClick={openPopup}>Talk to the team</button>
 */
export const useFilloutPopup = () => {
  const context = useContext(FilloutContext);
  
  if (!context) {
    throw new Error('useFilloutPopup must be used within a FilloutProvider');
  }
  
  return context;
};

export default FilloutContext;
